import { css } from '@emotion/react';

import { pxToEm } from './utils';

/*
 * BREAKPOINTS
 */
type Breakpoints = {
	mobileNav: string;
	xs: string;
	s: string;
	m: string;
	l: string;
	ml: string;
	xl: string;
};

export const breakpoints: Breakpoints = {
	mobileNav: typeof pxToEm(768),

	xs: pxToEm(10),
	s: pxToEm(640),
	m: pxToEm(768),
	l: pxToEm(1024),
	ml: pxToEm(1200),
	xl: pxToEm(1920),
};

/*
 * maps {key: value} to {key:'var(--NAME-KEY)'}
 */
function objToCssProps<T extends object = Record<string, unknown>>(
	obj: T,
	prefix: string = '',
): {
	[K in keyof T]: T[K] extends ResponsiveEntry ? string : T[K] extends object ? ReturnType<typeof objToCssProps<T[K]>> : string;
} {
	const result: Record<string, unknown> = {};
	Object.keys(obj).forEach((k) => {
		const key = k as keyof T;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((typeof obj[key] as any) === 'object' && (obj[key] as any).type !== 'responsive') {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			result[k] = objToCssProps(obj[key] as any, `${prefix}${k}-`);
		} else {
			result[k] = `--${prefix}${k}`;
		}
	});
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return result as any;
}

/*
 * maps {key: value} to '--NAME-KEY: value
 */
type ThemeValue = number | string | ResponsiveEntry;
type ThemeObjectDepth1 = Record<string, ThemeValue>;
type ThemeObjectDepth2 = Record<string, ThemeValue | ThemeObjectDepth1>;
type ThemeObjectDepth3 = Record<string, ThemeValue | ThemeObjectDepth1 | ThemeObjectDepth2>;
type ThemeObject = ThemeObjectDepth1 | ThemeObjectDepth2 | ThemeObjectDepth3;
function objToCss(obj: ThemeObject, name: string): string {
	return Object.keys(obj)
		.map((k) => {
			if (typeof obj[k] === 'object') {
				if ('type' in obj[k] && obj[k].type === 'responsive') {
					return [
						`--${name}-${k}: ${obj[k].s};`,
						`@${obj[k].for === 'container' ? 'container container' : 'media'} (min-width:${breakpoints.m}){--${name}-${k}: ${obj[k].m};}`,
						`@${obj[k].for === 'container' ? 'container container' : 'media'} (min-width:${breakpoints.l}){--${name}-${k}: ${obj[k].l};}`,
					].join('');
				}
				return objToCss(obj[k] as ThemeObject, `${name}-${k}`);
			}
			return `--${name}-${k}: ${obj[k]};`;
		})
		.join('');
}

type ResponsiveEntry = {
	type: 'responsive';
	for?: 'container';
	s: string | number;
	m: string | number;
	l: string | number;
};

/*
 * DIMENSIONS
 */
type Dimensions = {
	container: {
		padding: { x: ResponsiveEntry };
		margin: { y: ResponsiveEntry };
	};
	spacing: ResponsiveEntry;
	gap: ResponsiveEntry;
	maxWidth: string;
	maxWidthNarrow: string;
	header: {
		height: ResponsiveEntry;
		logoWidth: ResponsiveEntry;
	};
};
const dimensions: Dimensions = {
	container: {
		padding: { x: { type: 'responsive', s: pxToEm(12), m: pxToEm(20), l: pxToEm(60) } },
		margin: { y: { type: 'responsive', s: pxToEm(12), m: pxToEm(20), l: pxToEm(60) } },
	},
	spacing: { type: 'responsive', s: pxToEm(40), m: pxToEm(50), l: pxToEm(60) },
	gap: { type: 'responsive', for: 'container', s: pxToEm(20), m: pxToEm(30), l: pxToEm(60) },
	maxWidth: pxToEm(1200),
	maxWidthNarrow: pxToEm(820),
	header: {
		height: { type: 'responsive', s: pxToEm(80), m: pxToEm(120), l: pxToEm(200) },
		logoWidth: { type: 'responsive', s: pxToEm(50), m: pxToEm(80), l: pxToEm(120) },
	},
};

const zIndex = {
	mobileNav: 10,
	header: 20,
	overlayPage: 30,
	lightbox: 40,
	loadingScreen: 50,
	errorScreen: 60,
	decoration: 150,
};

/*
 * FONTS
 */
type Font = {
	family: string;
	weight: {
		thin: number;
		normal: number;
		bold: number;
	};
	size: {
		s: ResponsiveEntry;
		m: ResponsiveEntry;
		l: ResponsiveEntry;
		xl: ResponsiveEntry;
	};
	lineHeight: {
		s: ResponsiveEntry;
		m: ResponsiveEntry;
		l: ResponsiveEntry;
		xl: ResponsiveEntry;
	};
};
const lineHeight: Font['lineHeight']['s'] = { type: 'responsive', s: 1.1, m: 1.1, l: 1.1 };
const lineHeightSans: Font['lineHeight']['s'] = { type: 'responsive', s: 1.25, m: 1.25, l: 1.25 };
const serifFont: Font = {
	family: '"Crimson Pro Variable", serif',
	weight: {
		thin: 400,
		normal: 800,
		bold: 900,
	},
	size: {
		s: { type: 'responsive', s: pxToEm(14), m: pxToEm(14), l: pxToEm(18) },
		m: { type: 'responsive', s: pxToEm(18), m: pxToEm(22), l: pxToEm(26) },
		l: { type: 'responsive', s: pxToEm(24), m: pxToEm(30), l: pxToEm(38) },
		xl: { type: 'responsive', s: pxToEm(50), m: pxToEm(80), l: pxToEm(100) },
	},
	lineHeight: {
		s: lineHeight,
		m: lineHeight,
		l: lineHeight,
		xl: { type: 'responsive', s: 0.8, m: 0.8, l: 0.8 },
	},
};

const sansSerifFont: Font = {
	...serifFont,
	family: '"Inter Variable", sans-serif',
	weight: {
		thin: 300,
		normal: 400,
		bold: 600,
	},
	size: {
		s: { type: 'responsive', s: pxToEm(14), m: pxToEm(14), l: pxToEm(14) },
		m: { type: 'responsive', s: pxToEm(16), m: pxToEm(18), l: pxToEm(20) },
		l: { type: 'responsive', s: pxToEm(20), m: pxToEm(24), l: pxToEm(32) },
		xl: { type: 'responsive', s: pxToEm(50), m: pxToEm(80), l: pxToEm(100) },
	},
	lineHeight: {
		s: lineHeightSans,
		m: lineHeightSans,
		l: lineHeightSans,
		xl: lineHeightSans,
	},
};

/*
 * COLORS
 */
type ColorPalette = {
	fg: string;
	bg: string;
	fgContrast: string;
	bgContrast: string;
	grey1: string;
	grey2: string;
	grey3: string;
	grey4: string;
	grey1Contrast: string;
	grey2Contrast: string;
	grey3Contrast: string;
	grey4Contrast: string;
	primary: string;
	primaryDark: string;
	primaryLight: string;
	primaryContrast: string;
	primaryDarkContrast: string;
	primaryLightContrast: string;
	overlayBackground: string;
	overlayShadow: string;
	loadingBackground: string;
	link: string;
	linkActive: string;
	linkVisited: string;
	selection: string;
	selectionBackground: string;
	success: string;
	successContrast: string;
	warning: string;
	warningContrast: string;
	error: string;
	errorContrast: string;
};
export const colorPalettes = {};
export const colors = {
	white: '#fff',
	black: '#000',
	grey1: '#e3e3e3',
	grey2: '#a8a8a8',
	grey3: '#727272',
	grey4: '#3e3e3e',
	red: '#460a20',
	redDark: '#6a1436',
	redLight: '#df5285',
};
const light: ColorPalette = {
	fg: colors.black,
	bg: colors.white,

	fgContrast: colors.white,
	bgContrast: colors.black,

	grey1: colors.grey1,
	grey2: colors.grey2,
	grey3: colors.grey3,
	grey4: colors.grey4,

	grey1Contrast: colors.black,
	grey2Contrast: colors.black,
	grey3Contrast: colors.white,
	grey4Contrast: colors.white,

	primary: colors.red,
	primaryDark: colors.redDark,
	primaryLight: colors.redLight,
	primaryContrast: colors.white,
	primaryDarkContrast: colors.white,
	primaryLightContrast: colors.black,

	overlayBackground: 'rgba(255, 255, 255, .8)',
	overlayShadow: 'rgba(0,0,0, .4)',
	loadingBackground: 'transparent',

	link: colors.black,
	linkActive: 'color-mix(var(--color-link),var(--color-bg) 20%)',
	linkVisited: 'color-mix(var(--color-link),var(--color-bg) 20%)',

	selection: colors.black,
	selectionBackground: colors.red,

	success: '#3b9e3b',
	successContrast: '#fff',

	warning: '#ed8a15',
	warningContrast: '#000',

	error: '#ef1448',
	errorContrast: '#fff',
};

export const cssProps = objToCssProps({
	dimension: dimensions,
	zIndex,
	font: {
		serif: serifFont,
		sansSerif: sansSerifFont,
	},
	color: light,
});

export type CssProps = typeof cssProps;

export const globalStyles = [
	css`
		/* crimson-pro-latin-wght-normal */
		@font-face {
			font-family: 'Crimson Pro Variable';
			font-style: normal;
			font-display: swap;
			font-weight: 200 900;
			src: url(https://cdn.jsdelivr.net/fontsource/fonts/crimson-pro:vf@latest/latin-wght-normal.woff2) format('woff2-variations');
			unicode-range:
				U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191,
				U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
		}
		/* crimson-pro-latin-wght-italic */
		@font-face {
			font-family: 'Crimson Pro Variable';
			font-style: italic;
			font-display: swap;
			font-weight: 200 900;
			src: url(https://cdn.jsdelivr.net/fontsource/fonts/crimson-pro:vf@latest/latin-wght-italic.woff2) format('woff2-variations');
			unicode-range:
				U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191,
				U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
		}

		/* inter-latin-v0-wght-normal */
		@font-face {
			font-family: 'Inter Variable';
			font-style: normal;
			font-display: swap;
			font-weight: 100 900;
			src: url(https://cdn.jsdelivr.net/fontsource/fonts/inter:vf@latest/latin-wght-normal.woff2) format('woff2-variations');
			unicode-range:
				U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191,
				U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
		}

		:root {
			${objToCss(light, 'color')}
			${objToCss(zIndex, 'zIndex')}
			${objToCss(dimensions, 'dimension')}
			${objToCss(
				{
					serif: serifFont,
					sansSerif: sansSerifFont,
				},
				'font',
			)}
		}

		html,
		body {
			width: 100%;
			height: 100%;
			padding: 0;
			margin: 0;
			font-size: 100%;
			font-family: var(--font-serif-family);
			font-weight: var(--font-serif-weight-normal);
			font-style: normal;
			line-height: 1.2;
		}

		body {
			color: var(--color-fg);
			background-color: var(--color-bg);
			-moz-osx-font-smoothing: grayscale;
			font-size-adjust: none;
			-webkit-text-size-adjust: none;
			box-sizing: border-box;
			font-size: 100%;
			overflow-x: hidden;
			overflow-y: scroll;
		}

		::selection {
			background: var(--color-selectionBackground);
			color: var(--color-selection);
			text-shadow: none;
		}
	`,
];
